import React, {
  ChangeEvent,
  FC,
  useCallback,
  useMemo,
  MouseEvent,
  useState,
  useEffect,
} from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  Tree,
  TreeEventNodeEvent,
  TreeExpandedEvent,
  TreeFilterValueChangeEvent,
} from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import Grid from 'components/UI/Grid';
import Text from 'components/UI/Text';
import Tooltip from 'components/UI/Tooltip';
import { useStores } from 'stores/hooks/hooks';
import {
  ResellerTreeNode,
  CustomTreeNode,
  ResellerTreeMode,
} from 'models/grower';
import {
  TREE_NODE_HIDDEN_CLASS_NAME,
  TREE_NODE_INACTIVE_CLASS_NAME,
} from 'core/constants';
import { ToggleBox, ToggleBoxProps } from 'components/shared/ToggleBox';
import SearchInput from 'components/shared/SearchInput/SearchInput';
import debounce from 'lodash.debounce';
import Space from 'components/UI/Space';
import ResellersSearchTree from './ResellersSearchTree';

interface ResellerTreeProps {
  mode: ResellerTreeMode;
}

interface TemplateProps {
  node: TreeNode;
}

const StyledTree = styled(Tree)`
  flex: 1;
  flex-direction: column;
  display: flex;
  overflow: auto;
  border: none;
  padding: 0;

  .p-tree-container {
    display: flex;
    flex-direction: column;
    overflow: visible;
    height: 100%;
    width: 100%;
  }

  .p-treenode {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .p-highlight {
    z-index: 1;
  }

  .p-treenode-label {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .p-treenode-children {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .p-treenode-content {
    width: 100%;
  }

  .pi-fw {
    text-align: start;
  }

  .p-tree-header {
    visibility: hidden;
    height: 0px;
  }

  .${TREE_NODE_INACTIVE_CLASS_NAME} {
    .p-treenode-content {
      border-radius: 5px;
      background-color: var(--orange-200);
      color: black;
    }
  }

  .${TREE_NODE_HIDDEN_CLASS_NAME} {
    display: none;
  }
`;

enum TreeViewMode {
  TreeView = 'tree',
  SearchView = 'Search',
}

const ResellersTreeContainer = styled(Grid)`
  height: 100%;
  width: 100%;
  padding: 1rem;
  background-color: var(--surface-section);
  border: var(--surface-d);
`;

const Template: FC<TemplateProps> = ({ node }) => {
  const { label = '' } = node;
  return (
    <Tooltip arrow title={label} placement="top">
      <Text singleline>{label}</Text>
    </Tooltip>
  );
};

const ResellersTree: FC<ResellerTreeProps> = observer(({ mode, ...rest }) => {
  const { resellersStore } = useStores();
  const { t } = useTranslation('grower');
  // A grower-id deep link (e.g. the "grower id" link in the Users tab) must open
  // the List/search view, not the Tree: the Tree only loads the first ~100
  // growers, so a grower beyond that page showed "grower not found" (BUG 23846).
  // The List uses the uncapped /growers/search (by name or id), so seed it with
  // the grower id.
  const { deeplinkGrowerId } = resellersStore;

  const [filterValue, setFilterValue] = useState(
    deeplinkGrowerId != null ? String(deeplinkGrowerId) : '',
  );

  const [tabViewMode, setTabViewMode] = useState<TreeViewMode>(
    deeplinkGrowerId != null ? TreeViewMode.SearchView : TreeViewMode.TreeView,
  );

  // Retain the deep-link target for this mount so the List can auto-open the
  // grower once the search resolves (the store id itself is cleared below).
  const [deeplinkTargetId] = useState<number | null>(deeplinkGrowerId);

  if (!resellersStore.resellerTree) {
    return null;
  }

  const onSelect = useCallback(
    ({ node }: TreeEventNodeEvent) => {
      const isSameNodeSelected =
        node.data.id === resellersStore.selectedGrowerId;

      if (isSameNodeSelected) {
        return;
      }

      resellersStore.setSelectedTreeNode(
        node as CustomTreeNode<ResellerTreeNode>,
        mode,
      );

      if (node.children?.length) {
        resellersStore.addExpandedTreeNodes(node.key as string, mode);
      }
    },
    [mode, resellersStore],
  );

  const onToggle = useCallback(
    (e: TreeExpandedEvent) => {
      resellersStore.setExpandedTreeNodes(e.value, mode);
    },
    [mode, resellersStore],
  );

  const onActiveCheckboxChange = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, value: boolean) => {
      resellersStore.setTreeDisplayInactiveValue(value);
    },
    [resellersStore],
  );

  const handleFilterChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFilterValue(event.target.value);
    },
    [],
  );

  const handleTabViewChange = useCallback(
    (_event: MouseEvent<HTMLElement>, value: TreeViewMode) => {
      setTabViewMode((prev) => value ?? prev);
    },
    [],
  );

  const handleNothing = useCallback(
    (event: TreeFilterValueChangeEvent) => null,
    [],
  );

  const debouncedResults = useMemo(() => {
    return debounce(handleFilterChange, 300);
  }, [handleFilterChange]);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  // A grower-id deep link is one-shot: consume it once the view has been seeded
  // so a later remount / back-navigation doesn't force the List view again.
  useEffect(() => {
    if (resellersStore.deeplinkGrowerId != null) {
      resellersStore.setDeeplinkGrowerId(null);
    }
  }, [resellersStore]);

  const tableViewOptions: ToggleBoxProps['options'] = useMemo(
    () => [
      { value: TreeViewMode.TreeView, label: t('tree_tab_tree') },
      {
        value: TreeViewMode.SearchView,
        label: t('tree_tab_list'),
      },
    ],
    [t],
  );

  useEffect(() => {
    const controller = new AbortController();
    if (filterValue && tabViewMode === TreeViewMode.SearchView) {
      resellersStore.getResellerSearchTree(filterValue, controller);
    }

    return () => {
      controller.abort();
    };
  }, [filterValue, resellersStore, tabViewMode]);

  return (
    <ResellersTreeContainer align="stretch" overflow="auto" {...rest}>
      <SearchInput defaultValue={filterValue} onChange={debouncedResults} />
      <ToggleBox
        value={tabViewMode}
        options={tableViewOptions}
        onChange={handleTabViewChange}
      />
      <Space />
      {tabViewMode === TreeViewMode.SearchView ? (
        <ResellersSearchTree deeplinkGrowerId={deeplinkTargetId} />
      ) : (
        <StyledTree
          value={
            mode === ResellerTreeMode.All
              ? resellersStore.dataTree
              : resellersStore.dataResellerTree
          }
          filter
          // In order to create our own filter bar, we need to provide an onFilterValueChange prop (it does not need to be used!)
          onFilterValueChange={handleNothing}
          filterValue={filterValue}
          selectionMode="single"
          nodeTemplate={(node) => <Template node={node} />}
          selectionKeys={
            mode === ResellerTreeMode.All
              ? resellersStore.selectedTreeNodeKey
              : resellersStore.selectedResellerTreeNodeKey
          }
          onSelect={onSelect}
          onToggle={onToggle}
          expandedKeys={
            mode === ResellerTreeMode.All
              ? resellersStore.expandedTreeNodes
              : resellersStore.expandedResellerTreeNodes
          }
        />
      )}
      {tabViewMode === TreeViewMode.TreeView && (
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={resellersStore.isInactiveNodesVisible}
                onChange={onActiveCheckboxChange}
              />
            }
            label={<Text>{t('add_inactive')}</Text>}
          />
        </FormGroup>
      )}
    </ResellersTreeContainer>
  );
});

export default ResellersTree;
